import { useEffect, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from '@/components/ui/breadcrumb'
import { Separator } from '@/components/ui/separator'
import { SidebarTrigger } from '@/components/ui/sidebar'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { formatDate } from '@/lib/utils'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs'
import {
  fetchMaterials,
  fetchColors,
  fetchWireTypes,
  createColor,
  createWireType,
  updateMaterial,
  updateColor,
  updateWireType,
  deleteColor,
  deleteWireType,
  type UpdateMaterialPayload,
} from '@/services/materials'
import type { Material, MaterialColor, MaterialType } from '@/types/materials'
import {
  Plus,
  Check,
  AlertCircle,
  Package,
  Layers,
  Scale,
  Search,
  X,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Palette,
  Cable,
  Pencil,
  Trash2,
} from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Label } from '@/components/ui/label'

type SortField = 'name' | 'thickness' | 'weight' | 'color' | 'type' | 'created_at'
type SortDirection = 'asc' | 'desc' | null

function LoadingSkeleton() {
  return (
    <div className='space-y-2'>
      {[...Array(5)].map((_, i) => (
        <Skeleton key={i} className='h-16 w-full' />
      ))}
    </div>
  )
}

export default function Materials() {
  const { t, i18n } = useTranslation()
  const [materials, setMaterials] = useState<Material[]>([])
  const [colors, setColors] = useState<MaterialColor[]>([])
  const [wireTypes, setWireTypes] = useState<MaterialType[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingColors, setIsLoadingColors] = useState(true)
  const [isLoadingWireTypes, setIsLoadingWireTypes] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Filter state
  const [searchQuery, setSearchQuery] = useState('')
  const [filterColor, setFilterColor] = useState<string>('all')
  const [filterType, setFilterType] = useState<string>('all')

  // Sort state
  const [sortField, setSortField] = useState<SortField>('created_at')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')

  // Create state
  const [isColorSheetOpen, setIsColorSheetOpen] = useState(false)
  const [isWireTypeSheetOpen, setIsWireTypeSheetOpen] = useState(false)
  const [newColorName, setNewColorName] = useState('')
  const [newWireTypeName, setNewWireTypeName] = useState('')
  const [isCreatingColor, setIsCreatingColor] = useState(false)
  const [isCreatingWireType, setIsCreatingWireType] = useState(false)

  // Edit state
  const [editingColorId, setEditingColorId] = useState<number | null>(null)
  const [editingColorName, setEditingColorName] = useState('')
  const [editingWireTypeId, setEditingWireTypeId] = useState<number | null>(null)
  const [editingWireTypeName, setEditingWireTypeName] = useState('')
  const [isUpdating, setIsUpdating] = useState(false)

  // Delete state
  const [deletingColorId, setDeletingColorId] = useState<number | null>(null)
  const [deletingWireTypeId, setDeletingWireTypeId] = useState<number | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const handleCreateColor = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newColorName.trim()) return

    try {
      setIsCreatingColor(true)
      const newColor = await createColor(newColorName)
      setColors((prev) => [newColor, ...prev])
      setNewColorName('')
      setIsColorSheetOpen(false)
    } catch (err) {
      console.error('Failed to create color:', err)
    } finally {
      setIsCreatingColor(false)
    }
  }

  const handleCreateWireType = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newWireTypeName.trim()) return

    try {
      setIsCreatingWireType(true)
      const newType = await createWireType(newWireTypeName)
      setWireTypes((prev) => [newType, ...prev])
      setNewWireTypeName('')
      setIsWireTypeSheetOpen(false)
    } catch (err) {
      console.error('Failed to create wire type:', err)
    } finally {
      setIsCreatingWireType(false)
    }
  }

  // Color edit handlers
  const startEditingColor = (color: MaterialColor) => {
    setEditingColorId(color.id)
    setEditingColorName(color.name)
  }

  const cancelEditingColor = () => {
    setEditingColorId(null)
    setEditingColorName('')
  }

  const saveEditingColor = async (id: number) => {
    if (!editingColorName.trim()) return

    try {
      setIsUpdating(true)
      const updated = await updateColor(id, editingColorName)
      setColors((prev) => prev.map((c) => (c.id === id ? updated : c)))
      setEditingColorId(null)
      setEditingColorName('')
    } catch (err) {
      console.error('Failed to update color:', err)
    } finally {
      setIsUpdating(false)
    }
  }

  const handleDeleteColor = async () => {
    if (deletingColorId === null) return

    try {
      setIsDeleting(true)
      await deleteColor(deletingColorId)
      setColors((prev) => prev.filter((c) => c.id !== deletingColorId))
      setDeletingColorId(null)
    } catch (err) {
      console.error('Failed to delete color:', err)
    } finally {
      setIsDeleting(false)
    }
  }

  // Wire type edit handlers
  const startEditingWireType = (type: MaterialType) => {
    setEditingWireTypeId(type.id)
    setEditingWireTypeName(type.name)
  }

  const cancelEditingWireType = () => {
    setEditingWireTypeId(null)
    setEditingWireTypeName('')
  }

  const saveEditingWireType = async (id: number) => {
    if (!editingWireTypeName.trim()) return

    try {
      setIsUpdating(true)
      const updated = await updateWireType(id, editingWireTypeName)
      setWireTypes((prev) => prev.map((t) => (t.id === id ? updated : t)))
      setEditingWireTypeId(null)
      setEditingWireTypeName('')
    } catch (err) {
      console.error('Failed to update wire type:', err)
    } finally {
      setIsUpdating(false)
    }
  }

  const handleDeleteWireType = async () => {
    if (deletingWireTypeId === null) return

    try {
      setIsDeleting(true)
      await deleteWireType(deletingWireTypeId)
      setWireTypes((prev) => prev.filter((t) => t.id !== deletingWireTypeId))
      setDeletingWireTypeId(null)
    } catch (err) {
      console.error('Failed to delete wire type:', err)
    } finally {
      setIsDeleting(false)
    }
  }

  useEffect(() => {
    const loadMaterials = async () => {
      try {
        setIsLoading(true)
        setError(null)
        const data = await fetchMaterials()
        setMaterials(data)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load materials')
      } finally {
        setIsLoading(false)
      }
    }

    const loadColors = async () => {
      try {
        setIsLoadingColors(true)
        const data = await fetchColors()
        setColors(data)
      } catch (err) {
        console.error('Failed to load colors:', err)
      } finally {
        setIsLoadingColors(false)
      }
    }

    const loadWireTypes = async () => {
      try {
        setIsLoadingWireTypes(true)
        const data = await fetchWireTypes()
        setWireTypes(data)
      } catch (err) {
        console.error('Failed to load wire types:', err)
      } finally {
        setIsLoadingWireTypes(false)
      }
    }

    loadMaterials()
    loadColors()
    loadWireTypes()
    loadWireTypes()
  }, [])



  // Get unique colors and types for filters
  const uniqueColors = useMemo(() => {
    const colors = new Map<number, string>()
    materials.forEach((m) => colors.set(m.color.id, m.color.name))
    return Array.from(colors, ([id, name]) => ({ id, name }))
  }, [materials])

  const uniqueTypes = useMemo(() => {
    const types = new Map<number, string>()
    materials.forEach((m) => types.set(m.type.id, m.type.name))
    return Array.from(types, ([id, name]) => ({ id, name }))
  }, [materials])

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      if (sortDirection === 'asc') {
        setSortDirection('desc')
      } else if (sortDirection === 'desc') {
        setSortField('created_at')
        setSortDirection('desc')
      } else {
        setSortDirection('asc')
      }
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="ml-1 h-3 w-3" />
    }
    if (sortDirection === 'asc') {
      return <ArrowUp className="ml-1 h-3 w-3" />
    }
    if (sortDirection === 'desc') {
      return <ArrowDown className="ml-1 h-3 w-3" />
    }
    return <ArrowUpDown className="ml-1 h-3 w-3" />
  }

  const filteredMaterials = useMemo(() => {
    let result = materials.filter((item) => {
      const searchLower = searchQuery.toLowerCase()
      const matchesSearch =
        searchQuery === '' ||
        item.name.toLowerCase().includes(searchLower) ||
        item.color.name.toLowerCase().includes(searchLower) ||
        item.type.name.toLowerCase().includes(searchLower)

      const matchesColor =
        filterColor === 'all' || item.color.id.toString() === filterColor

      const matchesType =
        filterType === 'all' || item.type.id.toString() === filterType

      return matchesSearch && matchesColor && matchesType
    })

    if (sortField && sortDirection) {
      result = [...result].sort((a, b) => {
        let aValue: string | number
        let bValue: string | number

        switch (sortField) {
          case 'name':
            aValue = a.name
            bValue = b.name
            break
          case 'thickness':
            aValue = a.thickness
            bValue = b.thickness
            break
          case 'weight':
            aValue = a.weight
            bValue = b.weight
            break
          case 'color':
            aValue = a.color.name
            bValue = b.color.name
            break
          case 'type':
            aValue = a.type.name
            bValue = b.type.name
            break
          case 'created_at':
            // Using material creation date for consistency with original view, or we can use income created_at
            aValue = new Date(a.created_at).getTime()
            bValue = new Date(b.created_at).getTime()
            break
          default:
            return 0
        }

        if (typeof aValue === 'string' && typeof bValue === 'string') {
          const comparison = aValue.localeCompare(bValue)
          return sortDirection === 'asc' ? comparison : -comparison
        }

        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1

        return 0
      })
    }

    return result
  }, [materials, searchQuery, filterColor, filterType, sortField, sortDirection])

  const clearFilters = () => {
    setSearchQuery('')
    setFilterColor('all')
    setFilterType('all')
  }

  const hasActiveFilters = searchQuery !== '' || filterColor !== 'all' || filterType !== 'all'

  const formatWeight = (weight: number) => {
    return new Intl.NumberFormat('en-US', {
      maximumFractionDigits: 2,
    }).format(weight) + ' kg'
  }

  const totalMaterials = materials.length
  const totalWeight = materials.reduce((sum, m) => sum + m.weight, 0)
  const uniqueTypesCount = uniqueTypes.length

  return (
    <>
      <header className='flex h-16 shrink-0 items-center gap-2'>
        <div className='flex items-center gap-2 px-4'>
          <SidebarTrigger className='-ml-1' />
          <Separator
            orientation='vertical'
            className='mr-2 data-[orientation=vertical]:h-4'
          />
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbPage>{t('materials.title')}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      </header>

      {error && (
        <div className='mx-4 mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-400'>
          <AlertCircle className='h-4 w-4' />
          <span>{error}</span>
        </div>
      )}

      <div className='flex flex-1 flex-col gap-4 p-4 pt-0'>
        {/* Summary Cards */}
        <div className='grid gap-4 md:grid-cols-3'>
          <Card>
            <CardHeader className='flex flex-row items-center justify-between pb-2'>
              <CardTitle className='text-sm font-medium'>
                {t('materials.totalMaterials')}
              </CardTitle>
              <Package className='h-4 w-4 text-muted-foreground' />
            </CardHeader>
            <CardContent>
              <div className='text-2xl font-bold'>{totalMaterials}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className='flex flex-row items-center justify-between pb-2'>
              <CardTitle className='text-sm font-medium'>
                {t('materials.totalWeight')}
              </CardTitle>
              <Scale className='h-4 w-4 text-muted-foreground' />
            </CardHeader>
            <CardContent>
              <div className='text-2xl font-bold'>
                {formatWeight(totalWeight)}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className='flex flex-row items-center justify-between pb-2'>
              <CardTitle className='text-sm font-medium'>
                {t('materials.materialTypes')}
              </CardTitle>
              <Layers className='h-4 w-4 text-muted-foreground' />
            </CardHeader>
            <CardContent>
              <div className='text-2xl font-bold'>{uniqueTypesCount}</div>
            </CardContent>
          </Card>
        </div>

        {/* Tabbed Content */}
        <Tabs defaultValue='materials'>
          <TabsList>
            <TabsTrigger value='materials'>
              <Package className='mr-2 h-4 w-4' />
              {t('materials.materialsTab')}
            </TabsTrigger>
            <TabsTrigger value='colors'>
              <Palette className='mr-2 h-4 w-4' />
              {t('materials.colorsTab')}
            </TabsTrigger>
            <TabsTrigger value='wire-types'>
              <Cable className='mr-2 h-4 w-4' />
              {t('materials.wireTypesTab')}
            </TabsTrigger>
          </TabsList>

          {/* Materials Tab */}
          <TabsContent value='materials'>
            <Card>
              <CardHeader>
                <CardTitle>{t('materials.title')}</CardTitle>
                <CardDescription>
                  {t('materials.manageInventory')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Filters */}
                <div className='mb-4 flex flex-wrap items-center gap-3'>
                  <div className='relative flex-1 min-w-50'>
                    <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground' />
                    <Input
                      placeholder={t('materials.searchPlaceholder')}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className='pl-9'
                    />
                  </div>
                  <Select value={filterColor} onValueChange={setFilterColor}>
                    <SelectTrigger className='w-35'>
                      <SelectValue placeholder={t('materials.color')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='all'>{t('materials.allColors')}</SelectItem>
                      {uniqueColors.map((color) => (
                        <SelectItem key={color.id} value={color.id.toString()}>
                          {color.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={filterType} onValueChange={setFilterType}>
                    <SelectTrigger className='w-40'>
                      <SelectValue placeholder={t('materials.type')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='all'>{t('materials.allTypes')}</SelectItem>
                      {uniqueTypes.map((type) => (
                        <SelectItem key={type.id} value={type.id.toString()}>
                          {type.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {hasActiveFilters && (
                    <Button variant='ghost' size='sm' onClick={clearFilters}>
                      <X className='mr-1 h-4 w-4' />
                      {t('common.clear')}
                    </Button>
                  )}
                </div>

                {isLoading ? (
                  <LoadingSkeleton />
                ) : filteredMaterials.length === 0 ? (
                  <div className='py-8 text-center text-muted-foreground'>
                    {materials.length === 0
                      ? t('materials.noMaterialsFound')
                      : t('materials.noMaterialsMatch')}
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead
                          className='cursor-pointer select-none hover:bg-muted/50'
                          onClick={() => handleSort('name')}
                        >
                          <div className='flex items-center'>
                            {t('common.name')}
                            {getSortIcon('name')}
                          </div>
                        </TableHead>
                        <TableHead
                          className='cursor-pointer select-none hover:bg-muted/50'
                          onClick={() => handleSort('type')}
                        >
                          <div className='flex items-center'>
                            {t('materials.type')}
                            {getSortIcon('type')}
                          </div>
                        </TableHead>
                        <TableHead
                          className='cursor-pointer select-none hover:bg-muted/50'
                          onClick={() => handleSort('color')}
                        >
                          <div className='flex items-center'>
                            {t('materials.color')}
                            {getSortIcon('color')}
                          </div>
                        </TableHead>
                        <TableHead
                          className='cursor-pointer select-none hover:bg-muted/50 text-right'
                          onClick={() => handleSort('thickness')}
                        >
                          <div className='flex items-center justify-end'>
                            {t('materials.thickness')}
                            {getSortIcon('thickness')}
                          </div>
                        </TableHead>
                        <TableHead
                          className='cursor-pointer select-none hover:bg-muted/50 text-right'
                          onClick={() => handleSort('weight')}
                        >
                          <div className='flex items-center justify-end'>
                            {t('products.weight')}
                            {getSortIcon('weight')}
                          </div>
                        </TableHead>
                        <TableHead
                          className='cursor-pointer select-none hover:bg-muted/50'
                          onClick={() => handleSort('created_at')}
                        >
                          <div className='flex items-center'>
                            {t('common.created')}
                          </div>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredMaterials.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>
                            <div className='font-medium'>{item.name}</div>
                          </TableCell>
                          <TableCell>
                            <span className='rounded-full bg-muted px-2 py-1 text-xs font-medium'>
                              {item.type.name}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className='rounded-full bg-primary/10 text-primary px-2 py-1 text-xs font-medium'>
                              {item.color.name}
                            </span>
                          </TableCell>
                          <TableCell className='text-right'>
                            {item.thickness} mm
                          </TableCell>
                          <TableCell className='text-right font-medium'>
                              {formatWeight(item.weight)}
                          </TableCell>
                          <TableCell className='text-muted-foreground'>
                            {formatDate(item.created_at)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}

                {/* Results count */}
                {!isLoading && materials.length > 0 && (
                  <div className='mt-4 text-sm text-muted-foreground'>
                    {t('common.showing')} {filteredMaterials.length} {t('common.of')} {materials.length}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Colors Tab */}
          <TabsContent value='colors'>
            <Card>
              <CardHeader className='flex flex-row items-center justify-between'>
                <div>
                  <CardTitle>{t('materials.colors')}</CardTitle>
                  <CardDescription>
                    {t('materials.viewColors')}
                  </CardDescription>
                </div>
                <Sheet open={isColorSheetOpen} onOpenChange={setIsColorSheetOpen}>
                  <SheetTrigger asChild>
                    <Button>
                      <Plus className='mr-2 h-4 w-4' />
                      {t('materials.addColor')}
                    </Button>
                  </SheetTrigger>
                  <SheetContent>
                    <SheetHeader>
                      <SheetTitle>{t('materials.addNewColor')}</SheetTitle>
                      <SheetDescription>
                        {t('materials.createColor')}
                      </SheetDescription>
                    </SheetHeader>
                    <form
                      onSubmit={handleCreateColor}
                      className='flex flex-col gap-4 p-4'
                    >
                      <div className='space-y-2'>
                        <Label htmlFor='colorName'>{t('materials.colorName')}</Label>
                        <Input
                          id='colorName'
                          placeholder={t('materials.colorPlaceholder')}
                          value={newColorName}
                          onChange={(e) => setNewColorName(e.target.value)}
                          required
                        />
                      </div>
                      <SheetFooter>
                        <Button type='submit' disabled={isCreatingColor}>
                          {isCreatingColor ? t('common.loading') : t('common.create')}
                        </Button>
                      </SheetFooter>
                    </form>
                  </SheetContent>
                </Sheet>
              </CardHeader>
              <CardContent>
                {isLoadingColors ? (
                  <LoadingSkeleton />
                ) : colors.length === 0 ? (
                  <div className='py-8 text-center text-muted-foreground'>
                    {t('materials.noColorsFound')}
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('materials.id')}</TableHead>
                        <TableHead>{t('common.name')}</TableHead>
                        <TableHead>{t('common.created')}</TableHead>
                        <TableHead>{t('common.updated')}</TableHead>
                        <TableHead className='w-24'>{t('common.actions')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {colors.map((color) => (
                        <TableRow key={color.id}>
                          <TableCell className='font-medium'>
                            {color.id}
                          </TableCell>
                          <TableCell>
                            {editingColorId === color.id ? (
                              <div className='flex items-center gap-2'>
                                <Input
                                  value={editingColorName}
                                  onChange={(e) => setEditingColorName(e.target.value)}
                                  className='h-8 w-40'
                                  disabled={isUpdating}
                                />
                                <Button
                                  variant='ghost'
                                  size='icon'
                                  className='h-8 w-8'
                                  onClick={() => saveEditingColor(color.id)}
                                  disabled={isUpdating}
                                >
                                  <Check className='h-4 w-4 text-green-600' />
                                </Button>
                                <Button
                                  variant='ghost'
                                  size='icon'
                                  className='h-8 w-8'
                                  onClick={cancelEditingColor}
                                  disabled={isUpdating}
                                >
                                  <X className='h-4 w-4 text-muted-foreground' />
                                </Button>
                              </div>
                            ) : (
                              <span className='rounded-full bg-primary/10 text-primary px-2 py-1 text-xs font-medium'>
                                {color.name}
                              </span>
                            )}
                          </TableCell>
                          <TableCell className='text-muted-foreground'>
                            {formatDate(color.created_at)}
                          </TableCell>
                          <TableCell className='text-muted-foreground'>
                            {formatDate(color.updated_at)}
                          </TableCell>
                          <TableCell>
                            {editingColorId !== color.id && (
                              <div className='flex items-center gap-1'>
                                <Button
                                  variant='ghost'
                                  size='icon'
                                  className='h-8 w-8'
                                  onClick={() => startEditingColor(color)}
                                >
                                  <Pencil className='h-4 w-4' />
                                </Button>
                                <AlertDialog open={deletingColorId === color.id} onOpenChange={(open) => setDeletingColorId(open ? color.id : null)}>
                                  <Button
                                    variant='ghost'
                                    size='icon'
                                    className='h-8 w-8 text-destructive hover:text-destructive'
                                    onClick={() => setDeletingColorId(color.id)}
                                  >
                                    <Trash2 className='h-4 w-4' />
                                  </Button>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>{t('materials.deleteColor')}</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        {t('materials.deleteColorConfirm', { name: color.name })}
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                                      <AlertDialogAction
                                        onClick={handleDeleteColor}
                                        disabled={isDeleting}
                                        className='bg-destructive hover:bg-destructive/90'
                                      >
                                        {isDeleting ? t('common.loading') : t('common.delete')}
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}

                {!isLoadingColors && colors.length > 0 && (
                  <div className='mt-4 text-sm text-muted-foreground'>
                    {t('materials.totalColors', { count: colors.length })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Wire Types Tab */}
          <TabsContent value='wire-types'>
            <Card>
              <CardHeader className='flex flex-row items-center justify-between'>
                <div>
                  <CardTitle>{t('materials.wireTypes')}</CardTitle>
                  <CardDescription>
                    {t('materials.viewWireTypes')}
                  </CardDescription>
                </div>
                <Sheet open={isWireTypeSheetOpen} onOpenChange={setIsWireTypeSheetOpen}>
                  <SheetTrigger asChild>
                    <Button>
                      <Plus className='mr-2 h-4 w-4' />
                      {t('materials.addWireType')}
                    </Button>
                  </SheetTrigger>
                  <SheetContent>
                    <SheetHeader>
                      <SheetTitle>{t('materials.addNewWireType')}</SheetTitle>
                      <SheetDescription>
                        {t('materials.createWireType')}
                      </SheetDescription>
                    </SheetHeader>
                    <form
                      onSubmit={handleCreateWireType}
                      className='flex flex-col gap-4 p-4'
                    >
                      <div className='space-y-2'>
                        <Label htmlFor='wireTypeName'>{t('materials.wireTypeName')}</Label>
                        <Input
                          id='wireTypeName'
                          placeholder={t('materials.wireTypePlaceholder')}
                          value={newWireTypeName}
                          onChange={(e) => setNewWireTypeName(e.target.value)}
                          required
                        />
                      </div>
                      <SheetFooter>
                        <Button type='submit' disabled={isCreatingWireType}>
                          {isCreatingWireType ? t('common.loading') : t('common.create')}
                        </Button>
                      </SheetFooter>
                    </form>
                  </SheetContent>
                </Sheet>
              </CardHeader>
              <CardContent>
                {isLoadingWireTypes ? (
                  <LoadingSkeleton />
                ) : wireTypes.length === 0 ? (
                  <div className='py-8 text-center text-muted-foreground'>
                    {t('materials.noWireTypesFound')}
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('materials.id')}</TableHead>
                        <TableHead>{t('common.name')}</TableHead>
                        <TableHead>{t('common.created')}</TableHead>
                        <TableHead>{t('common.updated')}</TableHead>
                        <TableHead className='w-24'>{t('common.actions')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {wireTypes.map((type) => (
                        <TableRow key={type.id}>
                          <TableCell className='font-medium'>
                            {type.id}
                          </TableCell>
                          <TableCell>
                            {editingWireTypeId === type.id ? (
                              <div className='flex items-center gap-2'>
                                <Input
                                  value={editingWireTypeName}
                                  onChange={(e) => setEditingWireTypeName(e.target.value)}
                                  className='h-8 w-40'
                                  disabled={isUpdating}
                                />
                                <Button
                                  variant='ghost'
                                  size='icon'
                                  className='h-8 w-8'
                                  onClick={() => saveEditingWireType(type.id)}
                                  disabled={isUpdating}
                                >
                                  <Check className='h-4 w-4 text-green-600' />
                                </Button>
                                <Button
                                  variant='ghost'
                                  size='icon'
                                  className='h-8 w-8'
                                  onClick={cancelEditingWireType}
                                  disabled={isUpdating}
                                >
                                  <X className='h-4 w-4 text-muted-foreground' />
                                </Button>
                              </div>
                            ) : (
                              <span className='rounded-full bg-muted px-2 py-1 text-xs font-medium'>
                                {type.name}
                              </span>
                            )}
                          </TableCell>
                          <TableCell className='text-muted-foreground'>
                            {formatDate(type.created_at)}
                          </TableCell>
                          <TableCell className='text-muted-foreground'>
                            {formatDate(type.updated_at)}
                          </TableCell>
                          <TableCell>
                            {editingWireTypeId !== type.id && (
                              <div className='flex items-center gap-1'>
                                <Button
                                  variant='ghost'
                                  size='icon'
                                  className='h-8 w-8'
                                  onClick={() => startEditingWireType(type)}
                                >
                                  <Pencil className='h-4 w-4' />
                                </Button>
                                <AlertDialog open={deletingWireTypeId === type.id} onOpenChange={(open) => setDeletingWireTypeId(open ? type.id : null)}>
                                  <Button
                                    variant='ghost'
                                    size='icon'
                                    className='h-8 w-8 text-destructive hover:text-destructive'
                                    onClick={() => setDeletingWireTypeId(type.id)}
                                  >
                                    <Trash2 className='h-4 w-4' />
                                  </Button>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>{t('materials.deleteWireType')}</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        {t('materials.deleteWireTypeConfirm', { name: type.name })}
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                                      <AlertDialogAction
                                        onClick={handleDeleteWireType}
                                        disabled={isDeleting}
                                        className='bg-destructive hover:bg-destructive/90'
                                      >
                                        {isDeleting ? t('common.loading') : t('common.delete')}
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}

                {!isLoadingWireTypes && wireTypes.length > 0 && (
                  <div className='mt-4 text-sm text-muted-foreground'>
                    {t('materials.totalWireTypes', { count: wireTypes.length })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </>
  )
}
